<template>
  <div class="auth-page">
    <header class="top-bar">
      <div class="brand-pill" :style="{ backgroundColor: logoBackgroundColor }">
        <img
          v-if="clientLogo"
          :src="clientLogo"
          alt="Client Logo"
          class="brand-image" />
        <img
          v-else
          src="@/../public/static/img/acrew-logo-with-effects-white.png"
          alt="ACREW Logo"
          class="brand-image default" />
      </div>
      <div class="top-login">
        <img src="@/../public/static/img/acrew-dash-pattern.png" alt="" class="dash-pattern" />
        <span>Login</span>
      </div>
    </header>
    <div class="content-area">
      <div class="card-wrapper">
        <div class="auth-card">
          <div class="heading">
            <div class="heading-text">
              <h3>Please check your {{ contactMethod }} for a one time use code</h3>
            </div>
          </div>
          <div class="step-container">
            <div v-if="isResending" class="loading-overlay">
              <v-progress-circular indeterminate color="primary" size="50" />
            </div>
            <form @submit.prevent="verifyOTP">
              <v-otp-input v-model="otp_input"
                           length="4"
                           type="tel"
                           @finish="verifyOTP" />
              <p id="otp-helper" class="sr-only">Enter the 4 digit code sent to your {{ contactMethod }}.</p>
              <div>
                <div class="helper-section">
                  <span>Need a new code? </span>
                  <v-btn class="btn-outline-primary" type="button" :disabled="isResending" @click="resendOneTimePassword()">Resend code</v-btn>
                  <span v-if="contactMethod !== 'email'" class="or-text"> or </span>
                  <v-btn v-if="contactMethod !== 'email'" class="btn-outline-primary" type="button" :disabled="isResending" @click="resendOneTimePassword(true)">use email delivery</v-btn>
                </div>
                <v-btn class="btn-primary full-width-btn" type="submit" :disabled="isResending">
                  Submit
                </v-btn>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    <div v-if="clientColor" class="powered-by">
      <span>BROUGHT TO YOU BY</span>
      <img src="@/../public/static/img/acrew-logo-with-effects.png" alt="Logo" />
    </div>
  </div>
</template>
<style scoped lang="scss">
  /** TwoFactorAuthentication.vue */
  @import '@/scss/variables.scss';
  @import '@/scss/auth/page.scss';

  .step-container {
    position: relative;

    .loading-overlay {
      position: absolute;
      top: -20px;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
    }
  }

  .helper-section {
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    
    .or-text {
      margin: 0;
      color: #666;
    }

    @media (max-width: 500px) {
      flex-direction: column;
      text-align: center;
      gap: 12px;
      
      .btn-outline-primary {
        width: 100%;
      }
      
      .or-text {
        margin: 0;
      }
    }
  }

  .full-width-btn {
    width: 100%;
    margin-top: 5px;
  }
</style>
<script>
import DataService from '../../_services/DataService';
import { loadClientPreferences } from '../utils/UxUtils';

export default {
  name: 'TwoFactorAuthentication',
  data() {
    return {
      clientLogo: null,
      clientColor: null,
      whiteBehindLogo: false,
      otp_input: '',
      redirectURI: '',
      contactMethod: '',
      isResending: false,
    };
  },
  computed: {
    logoBackgroundColor() {
      return this.whiteBehindLogo ? 'white' : (this.clientColor || '#11405c');
    },
  },
  methods: {
    async resendOneTimePassword(forceEmail) {
      if (this.isResending) return;
      
      this.isResending = true;
      const twoFactorToken = localStorage.getItem('twoFactorToken');
      forceEmail = forceEmail || this.contactMethod === 'email';

      try {
        const response = await DataService.regenerate2FA(twoFactorToken, forceEmail);
        
        if (response.status === 200) {
          this.$root.$emit('raise_alert', { color: 'success', text: 'A new code has been sent' });
          localStorage.setItem('twoFactorToken', response.data.token); 
          this.contactMethod = response.data.sentSms === true ? 'text messages' : 'email';
        } else {
          throw new Error('Failed to regenerate code');
        }
      } catch (error) {
        this.$root.$emit('raise_alert', { color: 'error', text: 'Failed to send code. Please try again.' });
      } finally {
        this.isResending = false;
      }
    },
    async verifyOTP() {
      if (this.isResending) return;
      
      this.isResending = true;
      
      try {
        const twoFactorToken = localStorage.getItem('twoFactorToken');
        const response = await DataService.verifyOTP({ token: twoFactorToken, otp: this.otp_input });
        
        if (response.status === 200) {
          const { user, token, selectedClientId, clientIds, role, reportingRole } = response.data;
          DataService.updateSessionUser(user);
          DataService.updateClientId(selectedClientId);
          DataService.updateSessionRole(role);
          DataService.updateSessionReportingRole(reportingRole);

          localStorage.removeItem('twoFactorToken');
          localStorage.setItem('user', JSON.stringify(user));
          localStorage.setItem('clientIds', JSON.stringify(clientIds));
          localStorage.setItem('role', role);
          localStorage.setItem('reportingRole', reportingRole); 
          localStorage.setItem('token', token);

          this.$root.$emit('raise_alert', { color: 'success', text: 'Success, logging in...' });
          this.$router.push('/dashboard');
        } else {
          this.otp_input = '';
          this.$root.$emit('raise_alert', { color: 'error', text: 'Incorrect code. Please check your code and try again.' });
        }
      } catch (error) {
        this.otp_input = '';
        this.$root.$emit('raise_alert', { color: 'error', text: 'Network error. Please try again.' });
      } finally {
        this.isResending = false;
      }
    },
  },
  created() {
    if (!localStorage.getItem('twoFactorToken')) {
      this.$router.push('/login');
      return;
    }

    // check for redirect
    if (this.$route.query.redirectURI != undefined) {
      // URI is automatically decoded, so re-encode to pass on to dashboard page
      this.redirectURI = this.$route.query.redirectURI;
    } else {
      this.redirectURI = '/dashboard';
    }
  },
  async mounted() {
    try {
      // the query doesn't reliably type the sms
      this.contactMethod = `${this.$route.query.sentSms}` === 'true' ? 'text messages' : 'email';
      await loadClientPreferences(this);
      
      // Auto-focus the first OTP input
      this.$nextTick(() => {
        if (this.$refs.otpInput && this.$refs.otpInput.$el) {
          const firstInput = this.$refs.otpInput.$el.querySelector('input');
          if (firstInput) {
            firstInput.focus();
          }
        }
      });
    } catch (error) {
      this.$root.$emit('raise_alert', { color: 'error', text: 'Failed to initialize. Please refresh the page.' });
    }
  },
};
</script>
